import { Button, Divider, FormHelperText, Paper } from '@mui/material';
import { AxiosError } from 'axios';
import { Form, Formik, FormikHelpers } from 'formik';
import { find, map } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { fiatCurrencyExchangeApi, parsingMethodsApi } from 'api';
import {
  FormActions,
  FormControls,
  FormikSelect,
  FormikTextField,
} from 'components';
import { ParsingMethodType, QueryKey } from 'enums';
import { useCurrencies, useMutation } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { ParsingMethod, CreateParsingMethodDto, TradeMethod } from 'types';
import { validationUtils } from 'utils';

import { GenerateTemplateButton } from './GenerateTemplateButton';
import { ParsingMethodsTestForm } from './ParsingMethodsTestForm';

type Values = CreateParsingMethodDto;

type Props = {
  tradeMethod: TradeMethod | undefined;
  selectedParsingMethod?: ParsingMethod | null;
  withDescription?: boolean;
  tab: ParsingMethodType;
  onSuccess?: (values: ParsingMethod) => void;
};

export const ParsingMethodsForm: React.FC<Props> = ({
  tradeMethod,
  selectedParsingMethod = null,
  tab,
  onSuccess,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.parsing_methods',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { getFiatCurrencyExchangeLabel } = useCurrencies();
  const options = map(ParsingMethodType, (type) => ({
    value: type,
    label: t(`types.${type}`),
  }));

  // Sample SMS applied from the GPT generation form — feeds the test form so parsing runs right away.
  const [testMessage, setTestMessage] = useState('');

  const { data: fiatCurrencyExchanges } = useQuery(
    QueryKey.FiatCurrencyExchange,
    fiatCurrencyExchangeApi.getAll,
  );

  const fiatCurrencyExchangeOptions = useMemo(
    () =>
      map(fiatCurrencyExchanges, (exchange) => ({
        value: exchange.id,
        label:
          getFiatCurrencyExchangeLabel(
            exchange.fiatCurrencyId,
            exchange.crossFiatCurrencyId,
            exchange.source,
            exchange.name,
          ) + (exchange.enabled ? '' : ` - ${tCommon('common.disabled')}`),
      })),
    [fiatCurrencyExchanges, getFiatCurrencyExchangeLabel, tCommon],
  );

  const renderConversionHint = useCallback(
    (
      fiatCurrencyExchangeId: string | null | undefined,
      deviation: number | string | null | undefined,
    ) => {
      const exchange = find(fiatCurrencyExchanges, {
        id: fiatCurrencyExchangeId ?? '',
      });
      const baseFiatCurrencyId = tradeMethod?.fiatCurrencyId;

      // Cases where the backend (convertByFiatCurrencyExchange) returns null and does NOT convert.
      if (exchange && !exchange.enabled) {
        return (
          <FormHelperText error>
            {t('details_form.conversion_hint_disabled')}
          </FormHelperText>
        );
      }
      if (exchange && !(exchange.price > 0)) {
        return (
          <FormHelperText error>
            {t('details_form.conversion_hint_invalid_price')}
          </FormHelperText>
        );
      }
      if (
        exchange &&
        baseFiatCurrencyId &&
        baseFiatCurrencyId !== exchange.fiatCurrencyId &&
        baseFiatCurrencyId !== exchange.crossFiatCurrencyId
      ) {
        return (
          <FormHelperText error>
            {t('details_form.conversion_hint_currency_mismatch')}
          </FormHelperText>
        );
      }

      const rate = exchange ? exchange.price : '{{rate}}';
      // Mirror backend convertByFiatCurrencyExchange: divide when the method's base currency is on
      // the cross side of the pair, otherwise multiply.
      const isDivide =
        !!exchange && baseFiatCurrencyId === exchange.crossFiatCurrencyId;
      const operator = isDivide ? '/' : '×';
      return (
        <FormHelperText>
          {`{{amount}} ${operator} ${rate} ± ${Number(deviation) || 0}% → ${t(
            'details_form.conversion_hint',
          )}`}
        </FormHelperText>
      );
    },
    [fiatCurrencyExchanges, tradeMethod, t],
  );

  const initialValues: Values = useMemo(
    () => ({
      template: selectedParsingMethod?.template ?? '',
      tradeMethodId: tradeMethod?.id ?? '',
      type: selectedParsingMethod?.type ?? tab,
      description: selectedParsingMethod?.description ?? '',
      fiatCurrencyExchangeId:
        selectedParsingMethod?.fiatCurrencyExchangeId ?? '',
      deviation: selectedParsingMethod?.deviation ?? 0,
    }),
    [selectedParsingMethod, tradeMethod, tab],
  );

  const prepareParsingMethod = useCallback((values: Values) => {
    if (values.type !== ParsingMethodType.Technical) {
      values.description = null;
    }

    if (
      values.type !== ParsingMethodType.Payin ||
      !values.fiatCurrencyExchangeId
    ) {
      values.fiatCurrencyExchangeId = null;
      values.deviation = null;
    } else {
      values.deviation = Number(values.deviation) || 0;
    }

    return values;
  }, []);

  const createParsingMethod = useMutation<ParsingMethod, AxiosError, Values>(
    (parsingMethod) =>
      parsingMethodsApi.create(prepareParsingMethod(parsingMethod)),
  );

  const updateParsingMethod = useMutation<ParsingMethod, AxiosError, Values>(
    (parsingMethod) =>
      parsingMethodsApi.update({
        id: selectedParsingMethod!.id,
        data: prepareParsingMethod(parsingMethod),
      }),
  );

  const doSubmit = useMemo(() => {
    if (selectedParsingMethod) {
      return updateParsingMethod;
    }

    return createParsingMethod;
  }, [selectedParsingMethod, createParsingMethod, updateParsingMethod]);

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<any>) => {
      doSubmit.mutate(values, {
        onSuccess: (values: ParsingMethod) => {
          onSuccess?.(values);
        },
        onError: (error) => {
          formikHelpers.setErrors(validationUtils.getFormErrors(error));
        },
        onSettled: () => formikHelpers.setSubmitting(false),
      });
    },
    [onSuccess, doSubmit],
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, values }) => (
        <Fragment>
          <Form>
            <FormControls>
              <FormikTextField
                label={t('details_form.fields.template')}
                name="template"
              />
              <GenerateTemplateButton onApply={setTestMessage} />
              {values.type === ParsingMethodType.Technical && (
                <FormikTextField
                  label={t('details_form.fields.description')}
                  name="description"
                />
              )}
              <FormikSelect
                label={t('details_form.fields.type')}
                name="type"
                options={options}
              />
              {values.type === ParsingMethodType.Payin && (
                <Fragment>
                  <FormikSelect
                    label={t('details_form.fields.fiat_currency_exchange')}
                    name="fiatCurrencyExchangeId"
                    options={fiatCurrencyExchangeOptions}
                    noneOption
                  />
                  {renderConversionHint(
                    values.fiatCurrencyExchangeId,
                    values.deviation,
                  )}
                  {!!values.fiatCurrencyExchangeId && (
                    <FormikTextField
                      label={t('details_form.fields.deviation')}
                      name="deviation"
                      type="number"
                    />
                  )}
                </Fragment>
              )}
            </FormControls>
            <FormActions>
              <Button
                color="primary"
                variant="contained"
                type="submit"
                disabled={isSubmitting}
              >
                {tCommon('buttons.save')}
              </Button>
            </FormActions>
          </Form>
          <Divider sx={{ mt: 4 }} />
          <Paper sx={{ mt: 4 }}>
            <ParsingMethodsTestForm
              template={values.template}
              message={testMessage}
            />
          </Paper>
        </Fragment>
      )}
    </Formik>
  );
};
